import React, { useEffect, useState, useRef } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  getAssetLocations,
  createAssetLocation,
  updateAssetLocation,
  deleteAssetLocation,
} from '../api/asset-locationAPI';

const AssetLocationListLayer = () => {
  const userProfile = useUserProfile();
  const [locationList, setLocationList] = useState([]);
  const [newLocation, setNewLocation] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const fetchLocations = async () => {
    try {
      const data = await getAssetLocations();
      setLocationList(data);
    } catch (err) {
      console.error('Error fetching asset locations:', err);
    }
  };


  const validateForm = () => {
    const newErrors = {};
    if (!newLocation.assetId) newErrors.assetId = 'Asset ID is required';
    if (!newLocation.locationId) newErrors.locationId = 'Location ID is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (locationList.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: {
          paginate: { first: '', last: '', next: '', previous: '' },
        },
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [locationList]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewLocation((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (item) => {
    setNewLocation(item);
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this asset location?')) return;
    try {
      await deleteAssetLocation(id);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      await fetchLocations();
    } catch (error) {
      console.error('Error deleting asset location:', error);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      const payload = {};
      for (const key in newLocation) {
        if (newLocation[key] !== null && typeof newLocation[key] !== 'object') {
          payload[key] = newLocation[key];
        }
      }
      if (editingId) {
        await updateAssetLocation(editingId, payload);
      } else {
        await createAssetLocation(payload);
      }

      setShowModal(false);
      setEditingId(null);

      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }

      setNewLocation({});
      await fetchLocations();
    } catch (error) {
      console.error('Error saving asset location:', error);
    }
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {userProfile?.privileges?.includes('asset-locations:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={() => {
              setNewLocation({});
              setEditingId(null);
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add Asset Location
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0 w-100 rounded-0" ref={tableRef}>
            <thead>
              <tr>
                <th>Asset ID</th>
                <th>Location ID</th>
                <th>GPS Location</th>
                <th>Timestamp</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {locationList.map((item) => (
                <tr key={item.id}>
                  <td>{item.assetId}</td>
                  <td>{item.locationId}</td>
                  <td>{typeof item.gpsLocation === 'object' ? JSON.stringify(item.gpsLocation) : item.gpsLocation}</td>
                  <td>{item.locationTimestamp}</td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('asset-locations:update') && (
                        <button
                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleEdit(item)}
                        >
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('asset-locations:delete') && (
                        <button
                          className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Asset Location' : 'New Asset Location'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Asset ID *</label>
                    <input
                      className={`form-control ${errors.assetId ? 'is-invalid' : ''}`}
                      name="assetId"
                      value={newLocation.assetId || ''}
                      onChange={handleInputChange}
                    />
                    {errors.assetId && <div className="invalid-feedback">{errors.assetId}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Location ID *</label>
                    <input
                      className={`form-control ${errors.locationId ? 'is-invalid' : ''}`}
                      name="locationId"
                      value={newLocation.locationId || ''}
                      onChange={handleInputChange}
                    />
                    {errors.locationId && <div className="invalid-feedback">{errors.locationId}</div>}
                  </div>
                  <div className="col-md-12">
                    <label className="form-label">GPS Location</label>
                    <input
                      className="form-control"
                      name="gpsLocation"
                      value={newLocation.gpsLocation || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetLocationListLayer;
